import React from 'react'
import { Button, Card, Checkbox, DatePicker, Popconfirm, Select, Switch, Tag, Typography } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import debounce from 'lodash.debounce'
import { mainPagePoolsApi } from 'api/mainPagePools'
import { productApi } from 'api/product'
import { sellerApi } from 'api/seller'
import {
  EMainPagePoolPriority,
  EMainPagePoolType,
  IMainPagePool,
  IUpdateMainPagePool
} from 'models/mainPagePools'
import { EProductStatus } from 'models/product'
import { ISeller } from 'models/seller'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import { formatEndDateLeft, fromEndDateField, toEndDateField } from 'shared/utils/endDate'

const MAX_POOL_PRODUCTS = 10
const SEARCH_DEBOUNCE_MS = 500

const PRIORITY_OPTIONS = [
  { value: EMainPagePoolPriority.RANDOM, label: 'Рандом (по умолчанию)' },
  { value: EMainPagePoolPriority.PRICE_DESC, label: 'Самые дорогие' },
  { value: EMainPagePoolPriority.PRICE_ASC, label: 'Самые дешёвые' },
  { value: EMainPagePoolPriority.BIG_DISCOUNT, label: 'Большая скидка' }
]

interface ISelectOption {
  label: string
  value: string
}

// Черновик настроек одного пула в форме (до сохранения)
interface IPoolDraft {
  is_enabled: boolean
  type: EMainPagePoolType
  product_ids: string[]
  seller_id: string | null
  only_discounted: boolean
  no_duplicate: boolean
  priority: EMainPagePoolPriority
  end_date: Dayjs | null
}

const poolToDraft = (pool: IMainPagePool): IPoolDraft => ({
  is_enabled: pool.is_enabled,
  type: pool.type,
  product_ids: (pool.product_ids ?? []).map((p) => p._id),
  seller_id: pool.seller_id?._id ?? null,
  only_discounted: pool.only_discounted ?? false,
  no_duplicate: pool.no_duplicate ?? false,
  priority: pool.priority ?? EMainPagePoolPriority.RANDOM,
  end_date: toEndDateField(pool.end_date)
})

export const FeedSettings: React.FC = () => {
  const [isLoading, setIsLoading] = React.useState(false)
  const [pools, setPools] = React.useState<IMainPagePool[]>([])
  const [drafts, setDrafts] = React.useState<Record<number, IPoolDraft>>({})
  const [savingPosition, setSavingPosition] = React.useState<number | null>(null)

  // Справочник названий товаров для отображения выбранных значений в Select
  const [productOptions, setProductOptions] = React.useState<
    Record<string, string>
  >({})
  const [searchResults, setSearchResults] = React.useState<ISelectOption[]>([])
  const [isSearching, setIsSearching] = React.useState(false)
  const [sellers, setSellers] = React.useState<ISeller[]>([])

  const getPools = async () => {
    try {
      setIsLoading(true)
      const response = await mainPagePoolsApi.getPools()
      setPools(response.data)

      const nextDrafts: Record<number, IPoolDraft> = {}
      const knownProducts: Record<string, string> = {}
      response.data.forEach((pool) => {
        nextDrafts[pool.position] = poolToDraft(pool)
        ;(pool.product_ids ?? []).forEach((product) => {
          knownProducts[product._id] = product.article
            ? `${product.name} (арт. ${product.article})`
            : product.name
        })
      })
      setDrafts(nextDrafts)
      setProductOptions((prev) => ({ ...prev, ...knownProducts }))
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  const getSellers = async () => {
    try {
      const response = await sellerApi.getSearchSellers()
      setSellers(response.data)
    } catch (e) {
      showServerError(e)
    }
  }

  React.useEffect(() => {
    getPools()
    getSellers()
  }, [])

  const searchProducts = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([])
      return
    }
    try {
      setIsSearching(true)
      const response = await productApi.getProducts({
        skip: '0',
        limit: '20',
        name: query,
        status: EProductStatus.PUBLISHED
      })
      const options = response.data.products.map((product: any) => ({
        value: product._id,
        label: product.article
          ? `${product.name} (арт. ${product.article})`
          : product.name
      }))
      setSearchResults(options)
      setProductOptions((prev) => ({
        ...prev,
        ...Object.fromEntries(options.map((o: ISelectOption) => [o.value, o.label]))
      }))
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSearching(false)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchProducts = React.useCallback(
    debounce(searchProducts, SEARCH_DEBOUNCE_MS),
    []
  )

  const updateDraft = (position: number, patch: Partial<IPoolDraft>) => {
    setDrafts((prev) => ({
      ...prev,
      [position]: { ...prev[position], ...patch }
    }))
  }

  const savePool = async (position: number) => {
    const draft = drafts[position]
    if (!draft) return

    if (
      draft.is_enabled &&
      draft.type === EMainPagePoolType.PRODUCTS &&
      draft.product_ids.length === 0
    ) {
      return showMessage('info', 'Добавьте товары или выключите пул', '')
    }
    if (
      draft.is_enabled &&
      draft.type === EMainPagePoolType.SELLER &&
      !draft.seller_id
    ) {
      return showMessage('info', 'Выберите продавца или выключите пул', '')
    }
    if (draft.is_enabled && !draft.end_date) {
      return showMessage('info', 'Укажите дату окончания или выключите пул', '')
    }

    try {
      setSavingPosition(position)
      const body: IUpdateMainPagePool = {
        is_enabled: draft.is_enabled,
        type: draft.type,
        product_ids: draft.product_ids,
        seller_id: draft.seller_id,
        only_discounted: draft.only_discounted,
        no_duplicate: draft.no_duplicate,
        priority: draft.priority,
        end_date: fromEndDateField(draft.end_date)
      }
      await mainPagePoolsApi.updatePool(position, body)
      showMessage('success', 'Пул сохранён', '')
      await getPools()
    } catch (e) {
      showServerError(e)
    } finally {
      setSavingPosition(null)
    }
  }

  const addPool = async () => {
    try {
      await mainPagePoolsApi.createPool()
      await getPools()
    } catch (e) {
      showServerError(e)
    }
  }

  const deletePool = async (position: number) => {
    try {
      await mainPagePoolsApi.deletePool(position)
      await getPools()
    } catch (e) {
      showServerError(e)
    }
  }

  const sellerOptions: ISelectOption[] = sellers.map((seller) => ({
    value: seller._id,
    label: seller.shop_details?.shop_name ?? seller._id
  }))

  // index — номер пула в списке: позиции после удаления идут с пропусками
  const renderPool = (pool: IMainPagePool, index: number) => {
    const { position } = pool
    const draft = drafts[position]
    if (!draft) return null
    const endDateLeft = formatEndDateLeft(pool.end_date)

    const selectedProductOptions: ISelectOption[] = draft.product_ids.map(
      (id) => ({ value: id, label: productOptions[id] ?? id })
    )
    // выбранные товары держим в options всегда, иначе Select покажет голые id
    const mergedOptions = [
      ...selectedProductOptions,
      ...searchResults.filter(
        (option) => !draft.product_ids.includes(option.value)
      )
    ]

    // каждый включённый пул выше занимает два ряда по пять карточек
    const rowsHint = `ряды ${index * 2 + 1}-${index * 2 + 2} (карточки ${index * 10 + 1}-${index * 10 + 10})`

    return (
      <Card
        key={position}
        title={
          <span>
            Пул {index + 1} — {rowsHint}{' '}
            <Tag color={draft.is_enabled ? 'success' : 'default'}>
              {draft.is_enabled ? 'Включён' : 'Выключен'}
            </Tag>
            {endDateLeft && <Typography.Text type='secondary'>{endDateLeft}</Typography.Text>}
          </span>
        }
        extra={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 16 }}>
            {/* фильтр по скидке имеет смысл только для случайных товаров продавца:
                конкретные товары админ выбирает сам */}
            <Checkbox
              checked={draft.only_discounted}
              disabled={draft.type !== EMainPagePoolType.SELLER}
              onChange={(e) => {
                const checked = e.target.checked
                const patch: Partial<IPoolDraft> = { only_discounted: checked }
                // сняли фильтр скидок — «Большая скидка» теряет смысл, возвращаем рандом
                if (
                  !checked &&
                  draft.priority === EMainPagePoolPriority.BIG_DISCOUNT
                ) {
                  patch.priority = EMainPagePoolPriority.RANDOM
                }
                updateDraft(position, patch)
              }}
            >
              Только товары со скидкой
            </Checkbox>
            {/* один вид товара вместо карточки на каждый цвет/вариацию */}
            <Checkbox
              checked={draft.no_duplicate}
              onChange={(e) =>
                updateDraft(position, { no_duplicate: e.target.checked })
              }
            >
              Не дублировать товары
            </Checkbox>
            <Switch
              checked={draft.is_enabled}
              checkedChildren='Вкл'
              unCheckedChildren='Выкл'
              onChange={(checked) => updateDraft(position, { is_enabled: checked })}
            />
          </span>
        }
        style={{ marginBottom: 16 }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <Typography.Text strong>
              <span style={{ color: '#FF4D4F' }}>* </span>Дата окончания (срок размещения)
            </Typography.Text>
            <div style={{ marginTop: 4 }}>
              <DatePicker
                format='DD.MM.YYYY'
                style={{ width: 200 }}
                value={draft.end_date}
                disabledDate={(date) => date.isBefore(dayjs(), 'day')}
                onChange={(value) => updateDraft(position, { end_date: value })}
              />
            </div>
            <Typography.Text type='secondary'>После этой даты пул выключится.</Typography.Text>
          </div>

          <div>
            <Typography.Text strong>Источник товаров</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              value={draft.type}
              onChange={(type) => {
                const patch: Partial<IPoolDraft> = { type }
                // фильтр по скидке и «Большая скидка» доступны только пулу по продавцу
                if (type !== EMainPagePoolType.SELLER) {
                  patch.only_discounted = false
                  if (draft.priority === EMainPagePoolPriority.BIG_DISCOUNT) {
                    patch.priority = EMainPagePoolPriority.RANDOM
                  }
                }
                updateDraft(position, patch)
              }}
              options={[
                {
                  value: EMainPagePoolType.PRODUCTS,
                  label: 'Конкретные товары (до 10)'
                },
                {
                  value: EMainPagePoolType.SELLER,
                  label: 'Продавец (10 случайных товаров)'
                }
              ]}
            />
          </div>

          <div>
            <Typography.Text strong>Приоритет выдачи</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              value={draft.priority}
              onChange={(priority) => {
                const patch: Partial<IPoolDraft> = { priority }
                // «Большая скидка» = рандом среди скидочных → включаем фильтр скидок
                if (priority === EMainPagePoolPriority.BIG_DISCOUNT) {
                  patch.only_discounted = true
                }
                updateDraft(position, patch)
              }}
              options={PRIORITY_OPTIONS.map((option) =>
                option.value === EMainPagePoolPriority.BIG_DISCOUNT
                  ? {
                      ...option,
                      // «Большая скидка» опирается на фильтр скидок — только для пула по продавцу
                      disabled: draft.type !== EMainPagePoolType.SELLER
                    }
                  : option
              )}
            />
          </div>

          {draft.type === EMainPagePoolType.PRODUCTS ? (
            <div>
              <Typography.Text strong>
                Товары ({draft.product_ids.length}/{MAX_POOL_PRODUCTS})
              </Typography.Text>
              <Select
                mode='multiple'
                style={{ width: '100%', marginTop: 4 }}
                placeholder='Начните вводить название товара'
                value={draft.product_ids}
                options={mergedOptions}
                loading={isSearching}
                filterOption={false}
                onSearch={debouncedSearchProducts}
                onChange={(ids: string[]) => {
                  if (ids.length > MAX_POOL_PRODUCTS) {
                    return showMessage(
                      'info',
                      `В пуле максимум ${MAX_POOL_PRODUCTS} товаров`,
                      ''
                    )
                  }
                  updateDraft(position, { product_ids: ids })
                }}
              />
            </div>
          ) : (
            <div>
              <Typography.Text strong>Продавец</Typography.Text>
              <Select
                showSearch
                allowClear
                style={{ width: '100%', marginTop: 4 }}
                placeholder='Выберите продавца'
                value={draft.seller_id ?? undefined}
                options={sellerOptions}
                optionFilterProp='label'
                onChange={(sellerId?: string) =>
                  updateDraft(position, { seller_id: sellerId ?? null })
                }
              />
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Popconfirm title='Удалить пул?' onConfirm={() => deletePool(position)}>
              <Button danger>Удалить пул</Button>
            </Popconfirm>
            <Button
              type='primary'
              loading={savingPosition === position}
              onClick={() => savePool(position)}
            >
              Сохранить пул {index + 1}
            </Button>
          </div>
        </div>
      </Card>
    )
  }

  return (
    <div style={{ paddingBottom: 80 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24 }}>
        <Typography.Paragraph type='secondary'>
          Первые ряды товаров на главной странице сайта. Включённые пулы
          показываются над стандартной выдачей в порядке позиций, товары внутри
          пула перемешиваются при каждом обновлении страницы. Выключенный пул
          места не занимает — с первого ряда идёт стандартная выдача.
        </Typography.Paragraph>
        <Button type='primary' size='large' icon={<PlusOutlined />} onClick={addPool} style={{ flexShrink: 0 }}>
          Добавить пул
        </Button>
      </div>
      <Card loading={isLoading} style={{ border: 'none', background: 'transparent' }} bodyStyle={{ padding: 0 }}>
        {pools.map(renderPool)}
      </Card>
    </div>
  )
}
